/* global atob */
/**
 * A share that is bigger than a link.
 *
 * store.js already shares a kitchen, and does it the better way: the whole design deflated
 * into the URL fragment, which no server ever sees. That is still the default and nothing
 * here changes it. What a fragment cannot carry is the ROOM -- the scan of the kitchen as it
 * is today, the photographs the person brought, and the USDZ the LiDAR wrote, which is tens
 * of megabytes of mesh. Those are the three things a maker most wants beside the design, so
 * there is a second kind of share for when somebody asks for one: a package in a bucket,
 * behind an unguessable id, and a link that points at it.
 *
 * The trade is explicit and it is the person's to make: the design-only link keeps
 * everything on the device, and the hosted one does not. So `packageFor` takes three flags
 * and puts in exactly what was ticked -- and `modelPath` is never one of them, in any
 * combination, because it is a file:// path that says where on somebody's phone their scan
 * of their house lives. The USDZ travels as a FILE or it does not travel.
 *
 * ── THE CONTRACT PlannerPage WIRES ─────────────────────────────────────────────────────
 *
 * Making one (from ShareSheet.vue, which is the only thing that should call it):
 *
 *   const pkg = packageFor(plan, original, { includeOriginal, includePhotos, includeRoom })
 *   const { id, url } = await uploadShare(pkg, { roomPath: scanModel, onProgress })
 *   await shareLink(url)                       // src/kitchen/share.js
 *
 * Opening one, on boot:
 *
 *   if (location.pathname === '/open' && params.get('s')) {
 *     const { design, original, images, roomUrl } = await openShared(params.get('s'))
 *     takePlan(design, { remember: false })     // `design` is a deserialized plan, ready
 *   }
 *
 * `design` comes back already through `deserialize`, with `design.images` set to whatever
 * pictures the share carried, so `takePlan` takes it as-is. `original` is the room as it was
 * scanned, minus its `modelPath`, or null. `roomUrl` is an https URL for the USDZ, or null;
 * on an iPhone opening it stands the room on the floor at true size. `idFromSearch` reads
 * the id out of a query string, the way `codeFromHash` reads the code out of a fragment.
 *
 * Pure above the fetch line: `packageFor` touches nothing but its arguments, which is why
 * hosted.test.js can hold it to what it does and does not include.
 */
import { API_BASE } from '../core/native.js'
import { serialize, deserialize } from './store.js'
import { MAX_TOTAL, bytesOf } from './images.js'

/**
 * As much scan as this will carry.
 *
 * A RoomPlan USDZ of one kitchen is usually 5 to 25MB and can reach 60 with a lot of
 * texture. Eighty is above anything real and below the point where reading the file into a
 * JavaScript string is going to end the app on a phone -- base64 is a third bigger again,
 * so 80MB of mesh is about 190MB of peak memory between the string and the bytes. Past this
 * the person is told plainly rather than watching a progress line die.
 */
export const MAX_ROOM_BYTES = 80 * 1024 * 1024
const ROOM_TYPE = 'model/vnd.usdz+zip'

/**
 * What would be uploaded, given what was ticked. Pure -- no fetch, no clock but `madeAt`.
 *
 * Design is always in, and always without its pictures: they are carried once, in `images`,
 * where the Original's pictures join them, so a photo attached to both is stored once.
 *
 * @param {object} plan      the kitchen, as PlannerPage holds it
 * @param {object|null} original  the room as scanned, or null when this session did not scan
 * @param {{includeOriginal?: boolean, includePhotos?: boolean, includeRoom?: boolean}} opts
 */
export function packageFor(plan, original, opts = {}) {
  const { includeOriginal = false, includePhotos = false, includeRoom = false } = opts
  const pkg = {
    v: 1,
    madeAt: new Date().toISOString(),
    makerId: plan?.makerId ?? original?.makerId ?? null,
    design: serialize(plan ?? {}, { images: false }),
    original: null,
    images: [],
    files: {},
  }

  if (includeOriginal && original) {
    // Stripped here AND on the server. Two places, because this one is the one that has to
    // be right for the person to have been told the truth about what left their phone.
    const { modelPath: _modelPath, images: _images, ...rest } = original
    pkg.original = JSON.parse(JSON.stringify(rest))
  }

  if (includePhotos) pkg.images = capped([...(plan?.images ?? []), ...(original?.images ?? [])])

  // A name, not a path. The server writes the file at shares/<id>/room.usdz and this is how
  // the package says there is one; the device's own file:// path stays on the device.
  if (includeRoom) pkg.files.room = 'room.usdz'

  return pkg
}

/**
 * The pictures, deduplicated and held to the same 2MB the plan itself is held to.
 *
 * Over the cap the extra pictures are dropped rather than the share refused, because the
 * plan cannot hold more than 2MB in the first place -- getting here at all means the
 * Original brought pictures of its own, and the design's come first.
 */
function capped(images) {
  const seen = new Set()
  const out = []
  let total = 0
  for (const i of images) {
    if (!i || typeof i.dataUrl !== 'string') continue
    const id = i.id ?? i.dataUrl.slice(-40)
    if (seen.has(id)) continue
    const bytes = Number.isFinite(i.bytes) ? i.bytes : bytesOf(i.dataUrl)
    if (total + bytes > MAX_TOTAL) continue
    seen.add(id)
    total += bytes
    out.push({ ...i, bytes })
  }
  return out
}

/**
 * Put the package on the server and come back with the link.
 *
 * Two steps, and only ever two: the JSON through the API, and -- when the person asked for
 * the 3D scan -- the USDZ straight to the bucket over a resumable session URL the API hands
 * back. The file does not go through Cloud Run, which rejects request bodies over 32MB and
 * reports it as a bare 413 with nothing in anyone's logs.
 *
 * The size check happens BEFORE the POST. A scan that is too big to send should not leave a
 * half-made share sitting in a bucket for ninety days.
 *
 * @param {object} pkg  from `packageFor`
 * @param {{roomPath?: string|null, onProgress?: (line: string) => void}} opts
 * @returns {Promise<{id: string, url: string}>}
 */
export async function uploadShare(pkg, opts = {}) {
  const { roomPath = null, onProgress = null } = opts
  const wantRoom = !!pkg?.files?.room
  const say = (line) => { try { onProgress?.(line) } catch { /* the caller's problem */ } }

  if (wantRoom) {
    if (!roomPath) throw new Error('There is no scan file on this device to send.')
    const size = await roomSize(roomPath)
    if (size > MAX_ROOM_BYTES) {
      throw new Error(`That scan is ${Math.round(size / (1024 * 1024))} MB, and a share carries `
        + `${Math.round(MAX_ROOM_BYTES / (1024 * 1024))} MB at most. Share it as a file instead.`)
    }
  }

  say('Sending the design…')
  let res
  try {
    res = await fetch(`${API_BASE}/api/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        design: pkg.design,
        original: pkg.original,
        images: pkg.images,
        makerId: pkg.makerId,
        wantRoom,
      }),
    })
  } catch { throw new Error('Could not reach the server. Check the connection and try again.') }

  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.error ?? `The share could not be made (${res.status}).`)
  if (!body.id || !body.url) throw new Error('The server did not return a link.')

  if (wantRoom) {
    if (!body.room?.uploadUrl) throw new Error('The design was shared, but the server would not take the 3D scan.')
    say('Sending the 3D scan… this is the big one.')
    await putRoom(body.room.uploadUrl, roomPath)
  }

  return { id: body.id, url: body.url }
}

/**
 * Open a hosted share.
 *
 * @param {string} id  the 16-character id from `?s=`
 * @returns {Promise<{design: object, original: object|null, images: Array, roomUrl: string|null, madeAt: string|null, expiresAt: string|null}>}
 */
export async function openShared(id) {
  if (typeof id !== 'string' || !id) throw new Error('That link has no share in it.')
  let res
  try {
    res = await fetch(`${API_BASE}/api/share/${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } })
  } catch { throw new Error('Could not reach the server. Check the connection and try again.') }

  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.error ?? `That share could not be opened (${res.status}).`)

  const design = deserialize(body.design)
  if (!design) throw new Error('That link does not contain a kitchen this app can open.')
  const images = Array.isArray(body.images) ? body.images : []
  // Hung on the plan so `takePlan` needs no second step, and returned separately so a caller
  // that only wants the pictures does not have to go through the plan to reach them.
  design.images = images

  return {
    design,
    original: body.original ?? null,
    images,
    roomUrl: body.roomUrl ?? null,
    madeAt: body.madeAt ?? null,
    expiresAt: body.expiresAt ?? null,
  }
}

/** The id in a query string, if there is one: `?s=...`. The sibling of `codeFromHash`. */
export function idFromSearch(search) {
  const m = /(?:^|[?&])s=([A-Za-z0-9_-]{6,64})(?:&|$)/.exec(search ?? '')
  return m ? m[1] : null
}

// ── the file ───────────────────────────────────────────────────────────────────────────
//
// Native only, and it never becomes a Blob in JavaScript if it can be helped. Everything
// here is behind a dynamic import so the web build does not carry a Capacitor plugin it
// cannot run, which is the same rule share.js follows.

async function roomSize(path) {
  const { Filesystem } = await import('@capacitor/filesystem')
  try {
    const s = await Filesystem.stat({ path })
    return Number(s?.size) || 0
  } catch {
    // A scan we cannot measure is a scan we let through: the upload is the real test, and
    // refusing on a failed `stat` would block a share that would have worked.
    return 0
  }
}

/**
 * The USDZ, straight into the bucket.
 *
 * A GCS resumable session finishes in one PUT when the whole body goes at once, which is
 * what this does -- there is no chunking, because a phone that loses the connection halfway
 * through is better served by tapping Share again than by this file growing a resume
 * protocol nobody has tested.
 */
async function putRoom(uploadUrl, path) {
  const { Filesystem } = await import('@capacitor/filesystem')
  const read = await Filesystem.readFile({ path })
  const bytes = base64ToBytes(typeof read?.data === 'string' ? read.data : '')
  if (!bytes.length) throw new Error('The scan file could not be read from this device.')

  let res
  try {
    res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': ROOM_TYPE }, body: bytes })
  } catch { throw new Error('The 3D scan did not finish uploading. The link works without it.') }
  if (!res.ok) throw new Error(`The 3D scan did not upload (${res.status}). The link works without it.`)
}

function base64ToBytes(b64) {
  const clean = b64.includes(',') ? b64.slice(b64.indexOf(',') + 1) : b64
  const bin = atob(clean)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}
